import { FormEvent, useEffect, useRef, useState } from 'react'
import { usePage } from '@inertiajs/react'

type Status = 'waiting' | 'pending' | 'ready' | 'completed' | 'failed' | 'expired' | 'cancelled'
type WebhookAttempt = {
  id: number
  status: Status
  checked_count: number
  total_count: number
  failure_message: string | null
  expires_at: string | null
  delivery_accepted: boolean
  processing_status: 'not_received' | 'pending' | 'failed' | 'processed'
  local_billing_confirmation: boolean
}
const basePath = '/super_admin/stripe_setting'
const emptyForm = { webhook_secret: '', current_password: '', confirm_rotation: '' }
const terminal = ['completed', 'failed', 'expired', 'cancelled']
const buttonClass = 'px-4 py-2 rounded-lg border border-stone/30 text-sm font-semibold disabled:opacity-50'
class DisplayError extends Error {}

async function request(action: string, csrfToken: string, signal: AbortSignal, body?: object): Promise<WebhookAttempt | null> {
  const response = await fetch(`${basePath}/${action}`, { // allow-direct-fetch: JSON webhook update protocol uses shared Inertia CSRF and returns attempt status
    method: body ? 'POST' : 'GET',
    credentials: 'include',
    signal,
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
  const data = await response.json()
  // The backend's error field is sanitized for display. Never display raw response bodies.
  if (!response.ok) throw new DisplayError(typeof data?.error === 'string' ? data.error : 'Could not confirm webhook update status.')
  const attempt = data?.rotation
  if (attempt === null && !body) return null
  if (!attempt || !Number.isSafeInteger(attempt.id) || attempt.id <= 0 ||
      !['waiting', 'pending', 'ready', ...terminal].includes(attempt.status) ||
      !Number.isSafeInteger(attempt.checked_count) || attempt.checked_count < 0 ||
      !Number.isSafeInteger(attempt.total_count) || attempt.total_count < attempt.checked_count ||
      typeof attempt.delivery_accepted !== 'boolean' || typeof attempt.local_billing_confirmation !== 'boolean' ||
      !['not_received', 'pending', 'failed', 'processed'].includes(attempt.processing_status)) {
    throw new DisplayError('Could not confirm webhook update status. Check status before continuing.')
  }
  return attempt as WebhookAttempt
}

export default function StripeWebhookUpdate({ mode, webhookUrl }: { mode: 'test' | 'live'; webhookUrl: string | null }) {
  const sectionRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (window.location.hash === '#webhook-update') sectionRef.current?.scrollIntoView()
  }, [])
  const { csrf_token: csrfToken } = usePage().props as unknown as { csrf_token: string }
  const [form, setForm] = useState(emptyForm)
  const [attempt, setAttempt] = useState<WebhookAttempt | null>(null)
  const [known, setKnown] = useState(false)
  const [busy, setBusy] = useState(false)
  const [paused, setPaused] = useState(false)
  const [error, setError] = useState('')
  const [newAttempt, setNewAttempt] = useState(false)
  const lock = useRef(false)
  const controller = useRef<AbortController | null>(null)
  const advanceCount = useRef(0)
  const runRef = useRef<(action: string, body?: object) => Promise<void>>(async () => {})

  const run = async (action: string, body?: object) => {
    if (lock.current || !controller.current || controller.current.signal.aborted) return
    lock.current = true
    setBusy(true)
    setError('')
    const signal = controller.current.signal
    try {
      const next = await request(action, csrfToken, signal, body)
      if (signal.aborted) return
      if (body && action !== 'webhook_update' && next?.id !== bodyId(body)) {
        throw new DisplayError('Unexpected webhook attempt. Check status before continuing.')
      }
      setAttempt(next)
      setKnown(true)
      setPaused(false)
      if (action === 'webhook_update') { setNewAttempt(false); advanceCount.current = 0 }
      if (action === 'advance_webhook_update') {
        advanceCount.current += 1
        if (next?.status === 'pending' && (advanceCount.current >= 20 ||
            (next.checked_count === attempt?.checked_count && next.status === attempt.status))) {
          setPaused(true)
          setError(advanceCount.current >= 20
            ? 'Automatic verification limit reached. Check status to resume.'
            : 'Verification paused with no further checks confirmed. Check status to resume.')
        }
      }
    } catch (failure) {
      if (signal.aborted) return
      setKnown(false)
      setPaused(true)
      setError(failure instanceof DisplayError
        ? failure.message : 'Connection interrupted. Check status before trying again; the request may have succeeded.')
    } finally {
      lock.current = false
      if (!signal.aborted) setBusy(false)
    }
  }
  runRef.current = run

  useEffect(() => {
    const lifecycle = new AbortController()
    controller.current = lifecycle
    // Defer one microtask so StrictMode's discarded mount cannot acquire the lock.
    void Promise.resolve().then(() => {
      if (!lifecycle.signal.aborted) void runRef.current('webhook_update_status')
    })
    return () => { lifecycle.abort() }
  }, [])

  useEffect(() => {
    if (!known || busy || paused || !attempt) return
    const action = attempt.status === 'pending' ? 'advance_webhook_update'
      : ['waiting', 'completed'].includes(attempt.status) ? 'webhook_update_status' : null
    if (!action) return
    const timer = window.setTimeout(() => {
      void runRef.current(action, action === 'advance_webhook_update' ? { rotation_id: attempt.id } : undefined)
    }, attempt.status === 'pending' ? 1200 : 3000)
    return () => window.clearTimeout(timer)
  }, [attempt, known, busy, paused])

  const canStart = known && !paused && !busy && !!webhookUrl && !!csrfToken && (!attempt || (newAttempt && terminal.includes(attempt.status)))
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!canStart || form.confirm_rotation !== 'UPDATE WEBHOOK' || !form.webhook_secret || !form.current_password) return
    const submitted = { ...form }
    setForm(emptyForm)
    void run('webhook_update', { rotation: submitted })
  }
  const refresh = () => { advanceCount.current = 0; void run('webhook_update_status') }
  return <section ref={sectionRef} id="webhook-update" className="bg-white rounded-xl border border-stone/20 shadow-card p-6 space-y-4 scroll-mt-6">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-xl font-bold text-text-primary">Webhook-only update</h2>
      <span className="rounded-lg bg-stone/10 px-3 py-1 text-sm font-semibold text-text-primary">{mode === 'live' ? 'Live remains Live' : 'Test remains Test'}</span>
    </div>
    <p className="text-sm text-text-muted">Repair a signing-secret mismatch or activate a replacement webhook secret without changing the API key, account, mode, enrollment, or prices. This flow does not create charges or subscriptions.</p>
    <div className="rounded-lg border border-stone/30 p-4 text-sm text-text-primary space-y-2">
      <p><strong>Mismatch repair:</strong> use the current signing secret for this exact endpoint from Stripe. You do not need to roll the Stripe-side secret just to repair a mismatch.</p>
      <p><strong>Secret replacement:</strong> roll the signing secret on the existing Stripe endpoint first, using the runbook’s overlap procedure, then stage the replacement here. Updating Pinnacle does not roll or revoke the secret in Stripe.</p>
      <p><strong>Incident recovery:</strong> containment remains a runbook operation. This wizard does not contain a compromised secret, revoke access, or certify recovery.</p>
    </div>
    <div className="text-sm space-y-2">
      <p className="font-semibold text-text-primary">Configured webhook endpoint</p>
      {webhookUrl ? <code className="block rounded-lg bg-stone/10 px-3 py-2 break-all">{webhookUrl}</code>
        : <p role="alert">Trusted webhook URL is unavailable. Starting an update is disabled; restore the server configuration first.</p>}
      <p className="text-text-muted">Stage the secret below, then resend a recent event from this endpoint in the {mode === 'live' ? 'Live' : 'Test'} Stripe Dashboard. Do not use a CLI forwarding secret. Verification received is not the same as ready to save.</p>
    </div>
    {!known && busy && <div role="status" className="rounded-lg bg-stone/10 p-3 text-sm"><div className="h-2 w-2/3 bg-stone/20 rounded mb-2" />Checking latest owned webhook attempt…</div>}
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    {attempt && <div className="border border-stone/30 rounded-lg p-4 space-y-3 text-sm" aria-live="polite">
      <p className="font-semibold">Webhook attempt #{attempt.id}: {statusLabel(attempt.status)}</p>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
        <div><dt className="text-text-muted">Verification delivery</dt><dd>{attempt.delivery_accepted ? 'Received and accepted' : 'Not yet accepted'}</dd></div>
        <div><dt className="text-text-muted">Continuity checks</dt><dd>{attempt.checked_count} of {attempt.total_count} checked</dd></div>
        <div><dt className="text-text-muted">Worker processing</dt><dd>{processingLabel(attempt.processing_status)}</dd></div>
        <div><dt className="text-text-muted">Local billing confirmation</dt><dd>{attempt.local_billing_confirmation ? 'Confirmed' : 'Not yet confirmed'}</dd></div>
      </dl>
      {attempt.expires_at && !terminal.includes(attempt.status) && <p>Attempt expires: {new Date(attempt.expires_at).toLocaleString()}.</p>}
      {attempt.status === 'waiting' && <p>Waiting for a signed event at the configured endpoint. The stored signing secret has not been replaced.</p>}
      {attempt.status === 'pending' && <p>Verification received; continuity checks are still pending. Not ready to save.</p>}
      {attempt.status === 'ready' && <p>Ready to save. Verification has passed, but the staged signing secret is not active until you explicitly save.</p>}
      {attempt.status === 'completed' && <p className="font-semibold">Saved — signing secret activated. Accepted delivery alone does not confirm worker processing or local billing reconciliation. After activation, resend failed Checkout and subscription events from Stripe and confirm processing and the resulting local billing state before closing recovery.</p>}
      {attempt.status === 'failed' && <p role="alert">{attempt.failure_message || 'Verification failed. No unverified signing secret was activated.'}</p>}
      {attempt.status === 'expired' && <p>The attempt expired. Start a new attempt and resend a recent signed event.</p>}
      {attempt.status === 'cancelled' && <p>The staged update was cancelled; it was not activated.</p>}
      <div className="flex flex-wrap gap-2">
        {attempt.status === 'ready' && <button type="button" disabled={busy || !known || paused} onClick={() => void run('save_webhook_update', { rotation_id: attempt.id })} className={`${buttonClass} bg-pinnacle-red text-white`}>Save webhook signing secret</button>}
        {!terminal.includes(attempt.status) && <button type="button" disabled={busy || !known || paused} onClick={() => void run('cancel_webhook_update', { rotation_id: attempt.id })} className={buttonClass}>Cancel staged update</button>}
        {terminal.includes(attempt.status) && !newAttempt && <button type="button" disabled={busy || !known} onClick={() => { setNewAttempt(true); setForm(emptyForm) }} className={buttonClass}>Start a new webhook attempt</button>}
      </div>
    </div>}
    <button type="button" disabled={busy} onClick={refresh} className={buttonClass}>{busy ? 'Checking…' : paused ? 'Check status to resume' : 'Refresh webhook status'}</button>
    {(!attempt || newAttempt) && <form onSubmit={submit} className="space-y-4">
      <fieldset disabled={!canStart} className="space-y-4 disabled:opacity-60">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {([
            ['webhook_secret', 'Webhook signing secret', 'password'],
            ['current_password', 'Your current Super Admin password', 'password'],
            ['confirm_rotation', 'Type UPDATE WEBHOOK to stage verification', 'text'],
          ] as const).map(([name, label, type]) => <label key={name} className="block text-sm font-semibold text-text-primary">{label}
            <input required type={type} autoComplete={name === 'current_password' ? 'current-password' : 'off'} spellCheck={false} value={form[name]} onChange={event => setForm({ ...form, [name]: event.target.value })} className="block mt-1 w-full px-3 py-2 border border-stone/30 rounded-lg text-sm" />
          </label>)}
        </div>
        <p className="text-sm text-text-muted">Staging does not save the replacement. Password and secret clear on submission. Reloading resumes the server’s latest owned attempt; no secret or attempt ID is stored in this browser.</p>
        <button disabled={!canStart || form.confirm_rotation !== 'UPDATE WEBHOOK' || !form.webhook_secret || !form.current_password} className={`${buttonClass} bg-pinnacle-red text-white`}>Stage webhook update</button>
      </fieldset>
    </form>}
  </section>
}

function bodyId(body: object): unknown { return 'rotation_id' in body ? body.rotation_id : undefined }
function statusLabel(status: Status) {
  return { waiting: 'Waiting for verification', pending: 'Verification received — checks pending', ready: 'Ready to save', completed: 'Saved', failed: 'Failed', expired: 'Expired', cancelled: 'Cancelled' }[status]
}
function processingLabel(status: WebhookAttempt['processing_status']) {
  return { not_received: 'Not received by worker', pending: 'Pending', failed: 'Failed — review and resend', processed: 'Processed' }[status]
}
